import { Decimal } from "features/i18n/Percentage";
import "./QuestionGroupWeights.less";
import {
    qualityFormActions,
    selectGroupWeight,
    selectIsFormWeighted,
    selectQuestionGroupMaxTotalWeightedScore,
} from "features/qualityForm/qualityFormSlice";
import {
    selectIsScalingFactorPresent,
    selectIsScalingFactorUnderMaxLimit,
} from "features/qualityForm/qualityFormValidations";
import { useStrings } from "features/i18n/useStrings";

import { GuxFormFieldNumber, GuxTag } from "genesys-spark-components-react";
import { I18nComponent } from "react/utils/I18nComponent";
import strings from "./QuestionGroupWeights.strings";
import { ScoreTag } from "features/common/ScoreTag";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { PreviewModeContext } from "features/qualityForm/common/Contexts";
import { useContext, useEffect, useRef } from "react";
import { disableInputScroll } from "../common/DisableInputScroll";

/**
 * NOTE ABOUT THE SCALED POINTS OVERFLOW
 *
 * The MAX Total Scaled Points that we are fine in displaying
 * under the summary mode of main panel would be 1e+44 (total points) * 999,999 (scaling factor).
 * Above this number, edit and delete buttons would start being off the summary box.
 */

export function GroupScaledPointsViewer({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const isWeighted = useAppSelector(selectIsFormWeighted);
    const scaledPoints = useAppSelector((state) =>
        selectQuestionGroupMaxTotalWeightedScore(state, questionGroupId)
    );

    return (
        <>
            {isWeighted && (
                <span className="question-group-total-scaled-score">
                    <I18nComponent
                        i18nKey={
                            "qualityFormEditor.questionGroup.labels.totalScaledPoints"
                        }
                        substitutions={{
                            ScoreTag: (
                                <ScoreTag>
                                    <Decimal maxDigits={3}>
                                        {scaledPoints}
                                    </Decimal>
                                </ScoreTag>
                            ),
                        }}
                    />
                </span>
            )}
        </>
    );
}

export function GroupScoreScalingFactorViewer({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const questionGroupWeightValue =
        useAppSelector((state) => selectGroupWeight(state, questionGroupId)) ??
        0;
    const isWeighted = useAppSelector(selectIsFormWeighted);
    return (
        <>
            {isWeighted && (
                <span className="question-group-total-score-weight">
                    <I18nComponent
                        i18nKey={
                            "qualityFormEditor.questionGroup.labels.groupScoreScaleEditor"
                        }
                        substitutions={{
                            groupScoreScale: (
                                <GuxTag accent="4" emphasis="subtle">
                                    <i className="fa fa-balance-scale" />
                                    <Decimal maxDigits={3}>
                                        {questionGroupWeightValue}
                                    </Decimal>
                                </GuxTag>
                            ),
                        }}
                    />
                </span>
            )}
        </>
    );
}

export function GroupScoreScalingFactorInput({
    questionGroupId,
    label,
    labelPosition,
}: {
    questionGroupId: string;
    label: string;
    labelPosition: "screenreader" | "beside";
}) {
    const t = useStrings(strings);
    const { previewMode } = useContext(PreviewModeContext);
    const questionGroupWeightValue =
        useAppSelector((state) => selectGroupWeight(state, questionGroupId)) ??
        0;
    const errorMessage = useAppSelector(() => {
        if (!selectIsScalingFactorPresent(questionGroupWeightValue)) {
            return t.errors.emptyScalingFactor;
        }
        if (!selectIsScalingFactorUnderMaxLimit(questionGroupWeightValue)) {
            return t.errors.invalidScalingFactor;
        }
        return undefined;
    });
    const dispatch = useAppDispatch();

    const numberInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => disableInputScroll(numberInputRef));

    return (
        <GuxFormFieldNumber
            className="text-input"
            label-position={labelPosition}
        >
            <input
                slot="input"
                type="number"
                ref={numberInputRef}
                step="0.100"
                min="0"
                value={questionGroupWeightValue}
                onChange={({ currentTarget }) =>
                    dispatch(
                        qualityFormActions.setQuestionGroupWeight({
                            id: questionGroupId,
                            weight: currentTarget.valueAsNumber,
                        })
                    )
                }
                disabled={previewMode}
                aria-disabled={previewMode}
            ></input>
            <label slot="label">{label}</label>
            {errorMessage && (
                <span slot="error" data-testid="scaling-error" role="alert">
                    {errorMessage}
                </span>
            )}
        </GuxFormFieldNumber>
    );
}

export function GroupScoreScalingFactorEditor({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const t = useStrings(strings);
    return (
        <span className="question-group-total-score-weight">
            <I18nComponent
                i18nKey={
                    "qualityFormEditor.questionGroup.labels.groupScoreScaleEditor"
                }
                substitutions={{
                    groupScoreScale: (
                        <GroupScoreScalingFactorInput
                            questionGroupId={questionGroupId}
                            label={t.labels.groupScoreScaleEditorScreenReader}
                            labelPosition="screenreader"
                        />
                    ),
                }}
            />
        </span>
    );
}
